import { UseQueryResult } from '@tanstack/react-query'

import { KeyPaths } from '@fortem-monorepo/js-utilities'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { BetaScenario } from '@/pages/funds/ic-report/components/composites/BetaScenario'
import { BucketVsOvernight } from '@/pages/funds/ic-report/components/composites/BucketVsOvernight'
import { CapitalBarriers } from '@/pages/funds/ic-report/components/composites/CapitalBarriers'
import { CommodityCurves } from '@/pages/funds/ic-report/components/composites/CommodityCurves'
import { CtaExposure } from '@/pages/funds/ic-report/components/composites/CtaExposure'
import { DiversifiersScenario } from '@/pages/funds/ic-report/components/composites/DiversifierScenario'
import { FundLevelScenario } from '@/pages/funds/ic-report/components/composites/FundLevelScenario'
import { PerIssuer } from '@/pages/funds/ic-report/components/composites/PerIssuer'
import { ProtectionScenario } from '@/pages/funds/ic-report/components/composites/ProtectionScenario'
import { RatesVolatility } from '@/pages/funds/ic-report/components/composites/RatesVolatility'
import { BearBullChart } from '@/pages/funds/ic-report/components/primitives/charts/BearBullChart'
import { CommodityBCOMChart } from '@/pages/funds/ic-report/components/primitives/charts/CommodityBCOMChart'
import { CommodityBackwardationChart } from '@/pages/funds/ic-report/components/primitives/charts/CommodityBackwardationChart'
import { LockedYieldCharts } from '@/pages/funds/ic-report/components/primitives/charts/LockedYieldCharts'
import { Rebased0Chart } from '@/pages/funds/ic-report/components/primitives/charts/Rebased0Chart'
import { Rebased100Chart } from '@/pages/funds/ic-report/components/primitives/charts/Rebased100Chart'
import { RebasedNavChart } from '@/pages/funds/ic-report/components/primitives/charts/RebasedNavChart'
import { YearlyRebased0Chart } from '@/pages/funds/ic-report/components/primitives/charts/YearlyRebased0Chart'
import { AggregatedBreakdownTable } from '@/pages/funds/ic-report/components/primitives/tables/AggregatedBreakdownTable'
import { BetaOptionsTable } from '@/pages/funds/ic-report/components/primitives/tables/BetaOptionsTable'
import { BucketedAttributionsDiversifiersTable } from '@/pages/funds/ic-report/components/primitives/tables/BucketedAttributionsDiversifiersTable'
import { BucketedAttributionsTable } from '@/pages/funds/ic-report/components/primitives/tables/BucketedAttributionsTable'
import { DetailedBreakdownTable } from '@/pages/funds/ic-report/components/primitives/tables/DetailedBreakdownTable'
import { DiversifierMetricsTable } from '@/pages/funds/ic-report/components/primitives/tables/DiversifierMetricsTable'
import { EodCashBalanceChart } from '@/pages/funds/ic-report/components/primitives/tables/EodCashBalanceTable'
import { FundLevelMetricsTable } from '@/pages/funds/ic-report/components/primitives/tables/FundLevelMetricsTable'
import { FundLevelTable } from '@/pages/funds/ic-report/components/primitives/tables/FundLevelTable'
import { IncomeDurationTable } from '@/pages/funds/ic-report/components/primitives/tables/IncomeDurationTable'
import { LiquidityReportingTable } from '@/pages/funds/ic-report/components/primitives/tables/LiquidityReportingTable'
import { PerfContributionTable } from '@/pages/funds/ic-report/components/primitives/tables/PerfContributionTable'
import { ProtectionBreakdownTable } from '@/pages/funds/ic-report/components/primitives/tables/ProtectionBreakdownTable'
import { ProtectionOptionsTable } from '@/pages/funds/ic-report/components/primitives/tables/ProtectionOptionsTable'
import { RatingChangeTable } from '@/pages/funds/ic-report/components/primitives/tables/RatingChangeTable'
import { StressTestGRYTMinus1Table } from '@/pages/funds/ic-report/components/primitives/tables/StressTestGRYTMinus1Table'
import { StressTestGRYTTable } from '@/pages/funds/ic-report/components/primitives/tables/StressTestGRYTTable'
import { StressTestTable } from '@/pages/funds/ic-report/components/primitives/tables/StressTestTable'
import { TrackingDifferencesTable } from '@/pages/funds/ic-report/components/primitives/tables/TrackingDifferencesTable'
import { BaseICReportPrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { WidgetConstantGetter, labels } from '@/pages/funds/ic-report/constants'
import {
  beta,
  commodities,
  diversifiers,
  fundLevel,
  income,
  protection,
} from '@/react-query/queries/ic-report/hooks'

type BaseChildProps = {
  key: KeyPaths<ICReport.GetICReportTemplateData, boolean>
  sidebarTitle: React.ReactNode
  component: React.ComponentType<BaseICReportPrimitive>
}

type UniqueSectionChild = BaseChildProps & {
  unique: true
}

export type GenericSectionChild = BaseChildProps & {
  unique?: undefined
  labels: WidgetConstantGetter
  query: (...args: any[]) => UseQueryResult<any, any>
}

export type Section = {
  sectionTitle: React.ReactNode
  key: keyof ICReport.GetICReportTemplateData
  children: (UniqueSectionChild | GenericSectionChild)[]
}

export const FUND_LEVEL_SECTION: Section = {
  sectionTitle: 'Fund Level',
  key: 'fund_level',
  children: [
    {
      key: 'fund_level.summary',
      sidebarTitle: 'Summary',
      query: fundLevel.useFundLevel,
      component: FundLevelTable,
      labels: labels.fund_level.summary,
    },
    {
      key: 'fund_level.rebased_nav',
      sidebarTitle: 'Track Record',
      query: fundLevel.useRebasedNav,
      component: RebasedNavChart,
      labels: labels.fund_level.track_record_since_inception,
    },
    {
      key: 'fund_level.metrics',
      sidebarTitle: 'Metrics',
      query: fundLevel.useMetrics,
      component: FundLevelMetricsTable,
      labels: labels.fund_level.fund_performance_metrics,
    },
    {
      key: 'fund_level.buckets',
      sidebarTitle: 'Buckets',
      query: fundLevel.useBucketedAttributions,
      component: BucketedAttributionsTable,
      labels: labels.fund_level.buckets,
    },
    {
      key: 'fund_level.liquidity',
      sidebarTitle: 'Liquidity',
      query: fundLevel.useLiquidityReporting,
      component: LiquidityReportingTable,
      labels: labels.fund_level.liquidity,
    },
    {
      key: 'fund_level.tracking',
      sidebarTitle: 'Tracking',
      query: fundLevel.useTrackingDifferences,
      component: TrackingDifferencesTable,
      labels: labels.fund_level.tracking,
    },
    {
      key: 'fund_level.stress_test',
      sidebarTitle: 'Stress Test',
      query: fundLevel.useStressTest,
      component: StressTestTable,
      labels: labels.fund_level.stress_test,
    },
    {
      sidebarTitle: 'Scenario Analysis',
      key: 'fund_level.historical_events',
      component: FundLevelScenario,
      unique: true,
    },
  ],
} as const

export const INCOME_SECTION: Section = {
  sectionTitle: 'Income',
  key: 'income',
  children: [
    {
      key: 'income.duration',
      sidebarTitle: 'Duration',
      query: income.useDuration,
      component: IncomeDurationTable,
      labels: labels.income.duration,
    },
    {
      key: 'income.rating',
      sidebarTitle: 'Rating',
      query: income.useRatingChange,
      component: RatingChangeTable,
      labels: labels.income.rating,
    },
    {
      key: 'income.locked_yield',
      sidebarTitle: 'Locked Yield',
      query: income.useLockedYield,
      component: LockedYieldCharts,
      labels: labels.income.locked_yield,
    },
    {
      key: 'income.eod_cash',
      sidebarTitle: 'EOD Cash',
      query: income.useEodCash,
      component: EodCashBalanceChart,
      labels: labels.income.eod_cash,
    },
    {
      key: 'income.bucket_vs_overnight',
      sidebarTitle: 'Costs / Income',
      component: BucketVsOvernight,
      unique: true,
    },
    {
      key: 'income.per_issuer',
      sidebarTitle: 'Breakdown Per Issuer',
      component: PerIssuer,
      unique: true,
    },
  ],
} as const

export const BETA_SECTION: Section = {
  sectionTitle: 'Beta',
  key: 'beta',
  children: [
    {
      key: 'beta.detailed',
      sidebarTitle: 'Detailed',
      query: beta.useDetailedBreakdown,
      component: DetailedBreakdownTable,
      labels: labels.beta.detailed_regional_breakdown,
    },
    {
      key: 'beta.aggregated',
      sidebarTitle: 'Aggregated',
      query: beta.useAggregatedBreakdown,
      component: AggregatedBreakdownTable,
      labels: labels.beta.aggregated_by_region,
    },
    {
      key: 'beta.options',
      sidebarTitle: 'Options',
      query: beta.useOptions,
      component: BetaOptionsTable,
      labels: labels.beta.options,
    },
    {
      key: 'beta.gryt',
      sidebarTitle: 'GRY (T)',
      query: beta.useNewData,
      component: StressTestGRYTTable,
      labels: labels.beta.stresstest_gryt,
    },
    {
      key: 'beta.gryt-1',
      sidebarTitle: 'GRY (T-1)',
      query: beta.useOldData,
      component: StressTestGRYTMinus1Table,
      labels: labels.beta.stresstest_gryt_minus1,
    },
    {
      sidebarTitle: 'Barriers',
      key: 'beta.barriers',
      component: CapitalBarriers,
      unique: true,
    },
    {
      sidebarTitle: 'Scenario Analysis',
      key: 'beta.historical_events',
      component: BetaScenario,
      unique: true,
    },
  ],
} as const

export const DIVERSIFIERS_SECTION: Section = {
  sectionTitle: 'Diversifiers',
  key: 'diversifiers',
  children: [
    {
      key: 'diversifiers.attribution',
      sidebarTitle: 'Attribution',
      query: diversifiers.useBucketedAttributions,
      component: BucketedAttributionsDiversifiersTable,
      labels: labels.diversifiers.attribution,
    },
    {
      key: 'diversifiers.rebased_100',
      sidebarTitle: 'Factor Performance Attribution',
      query: diversifiers.useRebased100Monthly,
      component: Rebased100Chart,
      labels: labels.diversifiers.factor_performance_attribution_5yr,
    },
    {
      key: 'diversifiers.rebased_0_monthly',
      sidebarTitle: 'Monthly Performance Attribution',
      query: diversifiers.useRebased0Monthly,
      component: Rebased0Chart,
      labels: labels.diversifiers.monthly_performance_attribution_5yr,
    },
    {
      key: 'diversifiers.rebased_0_yearly',
      sidebarTitle: 'Yearly Performance Attribution',
      query: diversifiers.useRebased0Yearly,
      component: YearlyRebased0Chart,
      labels: labels.diversifiers.yearly_performance_attribution_5yr,
    },
    {
      key: 'diversifiers.perf_contribution',
      sidebarTitle: 'Perf Contribution',
      query: diversifiers.useContribution,
      component: PerfContributionTable,
      labels: labels.diversifiers.perf_contribution,
    },
    {
      key: 'diversifiers.metrics',
      sidebarTitle: 'Metrics',
      query: diversifiers.useMetrics,
      component: DiversifierMetricsTable,
      labels: labels.diversifiers.metrics,
    },
    {
      key: 'diversifiers.rate_vols',
      sidebarTitle: 'Rates Volatilities',
      query: diversifiers.useRatesVolatilities,
      component: RatesVolatility,
      labels: labels.diversifiers.us_rates_volatility_drivers,
    },

    {
      sidebarTitle: 'Scenario Analysis',
      key: 'diversifiers.historical_events',
      component: DiversifiersScenario,
      unique: true,
    },
    {
      sidebarTitle: 'CTA Exposure',
      key: 'diversifiers.cta_exposure',
      component: CtaExposure,
      unique: true,
    },
  ],
} as const

export const COMMODITIES_SECTION: Section = {
  sectionTitle: 'Commodities',
  key: 'commodities',
  children: [
    {
      key: 'commodities.curves',
      sidebarTitle: 'Curves',
      query: commodities.useCurves,
      component: CommodityCurves,
      labels: labels.commodities.curves,
    },
    {
      key: 'commodities.backwardation',
      sidebarTitle: 'Backwardation',
      query: commodities.useBackwardation,
      component: CommodityBackwardationChart,
      labels: labels.commodities.backwardation,
    },
    {
      key: 'commodities.bear_bull',
      sidebarTitle: 'Bear / Bull',
      query: commodities.useBearBull,
      component: BearBullChart,
      labels: labels.commodities.bear_bull,
    },
    {
      key: 'commodities.bcom',
      sidebarTitle: 'BCOM',
      component: CommodityBCOMChart,
      query: commodities.useHistoricalEventsBcom,
      labels: labels.commodities.scenario_analysis,
    },
  ],
} as const

export const PROTECTION_SECTION: Section = {
  sectionTitle: 'Protection',
  key: 'protection',
  children: [
    {
      key: 'protection.options',
      sidebarTitle: 'Options',
      query: protection.useOptions,
      component: ProtectionOptionsTable,
      labels: labels.protection.options,
    },
    {
      key: 'protection.breakdown',
      sidebarTitle: 'Breakdown',
      query: protection.useBreakdown,
      component: ProtectionBreakdownTable,
      labels: labels.protection.breakdown,
    },
    {
      sidebarTitle: 'Scenario Analysis',
      key: 'protection.scenario',
      component: ProtectionScenario,
      unique: true,
    },
  ],
}

export const SECTIONS = [
  FUND_LEVEL_SECTION,
  INCOME_SECTION,
  BETA_SECTION,
  DIVERSIFIERS_SECTION,
  COMMODITIES_SECTION,
  PROTECTION_SECTION,
] as const
